import type { CentralSeriesPoint, Granularity, AnalyticsPeriod } from '@/api/manticore';

// Number / unit formatting for the central CDN analytics surfaces.
// Mirrors the legacy `helpers.ts` / `business-usage-format.ts`.

const NUM_FMT = new Intl.NumberFormat('en-US');
const COMPACT_FMT = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

export function formatNum(n: number | string | undefined): string {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? NUM_FMT.format(Math.round(v)) : '—';
}

/** Compact count for chart axes/tooltips — "1.2M", "152B" (no thousands commas). */
export function formatCount(n: number | string | undefined): string {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? COMPACT_FMT.format(v) : '—';
}

/** Traffic volume bytes → human size (1000-based / SI). Traffic uses decimal
 * units (1 GB = 10^9 B), NOT the 1024 disk convention. 2 decimals for TB/GB,
 * 1 for MB/KB. */
export function formatBytes(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v) || v <= 0) return '0 B';
  const k = 1000;
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(v) / Math.log(k)));
  const dp = i >= 3 ? 2 : i >= 1 ? 1 : 0;
  return `${(v / Math.pow(k, i)).toFixed(dp)} ${units[i]}`;
}

/** Bytes → human size, **1000-based / SI** (matches CDN vendor dashboards):
 *  "113.15 MB", "2.31 GB". Use for traffic totals shown next to the vendor. */
export function formatBytesSI(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v) || v <= 0) return '0 B';
  const k = 1000;
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(v) / Math.log(k)));
  const dp = i >= 1 ? 2 : 0;
  return `${(v / Math.pow(k, i)).toFixed(dp)} ${units[i]}`;
}

/** Bytes → compact 1000-based size for axis ticks (no decimals): "113 MB". */
export function formatBytesAxisSI(n: number | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v) || v <= 0) return '0';
  const k = 1000;
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(v) / Math.log(k)));
  return `${Math.round(v / Math.pow(k, i))} ${units[i]}`;
}

/** Bytes → compact size for axis ticks (no decimals): "233 GB", "2 TB". */
export function formatBytesAxis(n: number | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v) || v <= 0) return '0';
  const k = 1000;
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(v) / Math.log(k)));
  return `${Math.round(v / Math.pow(k, i))} ${units[i]}`;
}

/** Bits-per-second → human rate (1000-based). */
export function formatBps(n: number | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v) || v <= 0) return '0 bps';
  const k = 1000;
  const units = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(v) / Math.log(k)));
  const dp = i >= 2 ? 2 : i >= 1 ? 1 : 0;
  return `${(v / Math.pow(k, i)).toFixed(dp)} ${units[i]}`;
}

/** Fraction or percent → "12.3%". `scale` says whether input is 0..1 or 0..100. */
export function formatPct(v: number | undefined, scale: '0..1' | '0..100' = '0..1', digits = 1): string {
  const n = Number(v ?? NaN);
  if (!Number.isFinite(n)) return '—';
  const pct = scale === '0..1' ? n * 100 : n;
  return `${pct.toFixed(digits)}%`;
}

/** Short axis/tooltip label for a bucket timestamp, tier-aware. */
export function fmtAxisTime(raw: string, granularity: Granularity): string {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  if (granularity === 'day') {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

// ---- Series reducers -----------------------------------------------------
type Pts = CentralSeriesPoint[] | undefined;

export function sumSeries(s: Pts): number {
  return (s ?? []).reduce((a, p) => a + (Number(p.value) || 0), 0);
}
export function maxSeries(s: Pts): number {
  return (s ?? []).reduce((a, p) => Math.max(a, Number(p.value) || 0), 0);
}
export function avgSeries(s: Pts): number {
  const pts = s ?? [];
  if (!pts.length) return 0;
  return sumSeries(pts) / pts.length;
}

/**
 * Caveat shown next to a peak-RATE figure (peak bandwidth / peak RPS) when the
 * window is rendered at the daily tier (7D / 30D / This month / Last month).
 *
 * Why the monthly peak reads higher than the live peak: a `day` bucket already
 * holds the SUM across domains of each domain's peak-of-day, and different
 * domains peak at different moments. Taking the MAX over those day buckets sums
 * peaks that never coincided, so it overstates the true instantaneous rate.
 * Finer tiers (1h / 5m) sum domains at the SAME instant first, then take the
 * max — that is the real simultaneous peak. Returns '' for non-day tiers
 * (1h/5m windows are already instantaneous, so no note is needed).
 */
export function peakRateDayCaveat(granularity?: Granularity | string): string {
  return granularity === 'day'
    ? 'Daily peaks summed across domains — a multi-day view adds each domain’s peak-of-day, so it reads higher than the live peak. Switch to 24H for the instantaneous peak.'
    : '';
}
/** Human label for a period, for sub-captions ("over last 24h"). */
export const PERIOD_LABEL: Record<AnalyticsPeriod, string> = {
  '1h': 'last 1h',
  '6h': 'last 6h',
  '24h': 'last 24h',
  today: 'today',
  last_day: 'yesterday',
  '7d': 'last 7d',
  '30d': 'last 30d',
  this_month: 'this month',
  last_month: 'last month',
};
